import { FormRows } from '@acorns/web-components'
import * as Sentry from '@sentry/browser'
import format from 'date-fns/format'
import { pathOr } from 'ramda'
import { addMethod, object, string } from 'yup'

import { ActionType, PageName } from 'generated/types'
import morning from 'utils/date/morning'
import { canPrePopulate, validateDob } from 'utils/date/validate'
import { sendToSentry, transformErrors } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { displayErrorModal, parseGraphQLError } from 'utils/form-utils'
import { getGeneralError } from 'utils/getGeneralError'
import { dobMask } from 'utils/text-masks/dob'
import { noSpecialCharacters } from 'utils/validationSchema'

// Backend expects YYYY/MM/DD
const formatDob = (dob) => format(dob, 'YYYY/MM/DD')

noSpecialCharacters()

addMethod(string, 'dob', function () {
  return this.test('dob', null, function (value) {
    const { path, createError } = this

    const message = validateDob(value, morning())

    if (message === true) {
      return message
    }

    return createError({
      message,
      path,
    }) as any
  })
})

export const handleSubmit = (values, { props, ...ownProps }) => {
  ownProps.setSubmitting(true)
  Sentry.addBreadcrumb({ message: 'User attempting to submit personal info at /personal-info' })
  const variables = {
    ...values,
    dob: formatDob(values.dob),
  }

  return props
    .updatePersonalInfo({ variables })
    .then(() => {
      props.isDynamicFunnelEnabled
        ? props.routeToPage(PageName.PERSONAL_INFO, ActionType.CONTINUE)
        : props.route({ from: FlowRoute.PersonalInfo, action: 'continue' })
    })
    .catch((error) => {
      const retryHandler = () => {
        props.closeErrorModal()
        ownProps.submitForm()
      }
      const { title, message, actions, image } = getGeneralError(error, props, retryHandler)
      props.showErrorModal({ title, message, actions, image })

      sendToSentry(error)
      ownProps.setSubmitting(false)
      const errorCode = parseGraphQLError(error)

      displayErrorModal({
        ...props,
        errorCode,
        errorCodeMap: {},
      })
    })
}

const allFields = new Map([
  [
    'firstName',
    string()
      .required('Please enter a valid first name.')
      .max(32, 'Your first name cannot exceed ${max} characters.')
      .matches(/^((?!&|\sand\s).)*$/, 'Please only enter your legal first name.')
      .matches(/^[^\d]*$/, 'Please do not enter numbers.'),
  ],
  [
    'lastName',
    string()
      .required('Please enter a valid last name.')
      .max(32, 'Your last name cannot exceed ${max} characters.')
      .matches(/^((?!&|\sand\s).)*$/, 'Please only enter your legal last name.')
      .matches(/^[^\d]*$/, 'Please do not enter numbers.'),
  ],
  ['dob', string().required('Please enter a valid birthday.').dob()],
  [
    'phoneNumber',
    string()
      .required('Please enter a valid phone number.')
      .matches(
        /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
        'Please enter a valid phone number.',
      )
      .test('phoneNumberTest', 'Please enter a valid phone number.', (value) => {
        return !new RegExp(/^\((\d)\1{2}\) \1{3}-\1{4}$/).test(value)
      }),
  ],
])

const validationSchema = (fieldNames: string[]) => {
  const fields: any = {}

  fieldNames.map((fieldName) => {
    fields[fieldName] = allFields.get(fieldName)
  })

  return object().shape(fields)
}

// Handles Formik's validate event.
export const validate = (values) => {
  let errors = {}

  try {
    validationSchema(Object.keys(values)).validateSync(values, { abortEarly: false })
  } catch (validationErrors) {
    // Convert validation errors to Formik errors collection
    errors = transformErrors(validationErrors.inner)
  }

  return errors
}

export const mapPropsToValues =
  ({ showPhoneNumber, showNames }) =>
  ({ data, plaidLayerData }) => {
    const allowPrePopulate = canPrePopulate(pathOr(null, ['profile', 'createdAt'], data))
    const source = data.user || {}

    const values: any = (({ dob }) => ({
      dob: allowPrePopulate && dob ? format(dob, 'MM/DD/YYYY') : '',
    }))(source)

    if (plaidLayerData) {
      return {
        ...values,
        dob: values.dob || plaidLayerData?.dateOfBirth,
        firstName: values.firstName || plaidLayerData?.firstName,
        lastName: values.lastName || plaidLayerData?.lastName,
      }
    }

    if (showPhoneNumber) {
      values.phoneNumber = (allowPrePopulate && source.phoneNumber) || ''
    }

    if (showNames) {
      values.firstName = (allowPrePopulate && source.firstName) || ''
      values.lastName = (allowPrePopulate && source.lastName) || ''
    } else if (!source.firstName || !source.lastName) {
      values.firstName = source.firstName || ''
      values.lastName = source.lastName || ''
    }

    return values
  }

export default [
  [
    {
      label: 'Legal First Name *',
      name: 'firstName',
      type: 'text',
      placeholder: 'John',
    },
    {
      label: 'Legal Last Name *',
      name: 'lastName',
      type: 'text',
      placeholder: 'Doe',
    },
  ],
  [
    {
      label: 'Birthday *',
      name: 'dob',
      mask: dobMask,
      placeholder: 'MM/DD/YYYY',
      type: 'text',
      guide: false,
    },
  ],
] as FormRows
